/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import { useTranslation } from "react-i18next";

import { useEffect, useState } from "react";
import { MediaBatchPreparationInitServices } from "../../../../../service/mediaBatchPrepInitServices";
import {
	BatchCreationPayload,
	MasterActivities,
	MasterActivitiesResponseList,
	MediaMasters,
	MixingRatioResponse,
} from "../../types/mediaBatchCreation.types";
import MBCProfileCard from "./partials/MBCProfileCard";
import MBCFooter from "./partials/MBCFooter";
import CustomToast from "../../../../../widgets/toaster";
import { useMediaBatchPrepView } from "../../../../../hooks/useMediaBatchPrepView";

interface Props {
	onNext: () => void;
	setBatchId: (batchId: number) => void;
	batchId: number | null; // Changed to string to match your earlier interfaces
}

const MediaBatchCreation = ({ onNext, setBatchId, batchId }: Props) => {
	const { t } = useTranslation();

	const { mediaBatchData, fetchBatchDetails } = useMediaBatchPrepView();
	const [mediaMasterList, setMediaMasterList] = useState<MediaMasters[]>([]);
	const [masterActivitiesList, setMasterActivitiesList] = useState<
		MasterActivities[]
	>([]);
	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error",
	});

	const [errors, setErrors] = useState({
		media_batch_description: "",
		media_master_id: "",
		intended_use_id: "",
	});

	const validateFields = () => {
	const newErrors = {
		media_batch_description: "",
		media_master_id: "",
		intended_use_id: "",
	};
	let valid = true;
	if (!creationPayload.media_batch_description.trim()) {
		newErrors.media_batch_description = t("mediaBatchInit.errors.batchDesc");
		valid = false;
	}

	if (!creationPayload.media_master_id) {
		newErrors.media_master_id = t("mediaBatchInit.errors.mediaType");
		valid = false;
	}

	if (!creationPayload.intended_use_id) {
		newErrors.intended_use_id = t("mediaBatchInit.errors.intendedUse");
		valid = false;
	}

	setErrors(newErrors);
	return valid;
	};


	const [creationPayload, setCreationPayload] = useState<BatchCreationPayload>(
		{
			media_batch_description: "",
			media_master_id: "",
			intended_use_id: "",
			sop_mixing_ratio: "",
			remarks:"",
		},
	);

	const isReadOnly = !!mediaBatchData;

	const getMediaMasterList = async () => {
		const urlParams = { sort: "id", order: "desc", source_type: "INH" };
		try {
			const response =
				await MediaBatchPreparationInitServices.getMediaMasters(urlParams);
			setMediaMasterList(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getMasterActivities = async () => {
		const urlParams = { sort: "sort", order: "asc", status: "active" };
		try {
			const response: MasterActivitiesResponseList =
				await MediaBatchPreparationInitServices.getMediaMasterActivities(
					urlParams,
				);
			setMasterActivitiesList(response.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getMixingRatio = async () => {
		let masterId = creationPayload.media_master_id;
		let intendedUseId = creationPayload.intended_use_id;

		if (masterId === "" || intendedUseId === "" || isReadOnly) return;

		const urlParams = {
			sort: "id",
			order: "desc",
			media_type_id: masterId,
			intended_use_id: intendedUseId,
		};

		try {
			const response: MixingRatioResponse =
				await MediaBatchPreparationInitServices.getMixingRatio(urlParams);
			setCreationPayload((prev) => ({
				...prev,
				sop_mixing_ratio: response.data[0]?.media_solution_mix_ratio,
			}));
		} catch (error) {
			console.error(error);
		}
	};

	const handleChange = (key: string, value: string) => {
		setCreationPayload((prev) => ({ ...prev, [key]: value }));
	    setErrors((prev) => ({ ...prev, [key]: "",}));
	};

	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	useEffect(() => {
		// Always fetch the dropdown lists so IDs can map to names properly
		getMediaMasterList();
		getMasterActivities();

		// If batchId exists, fetch the data to populate the read-only view
		if (batchId) {
			console.log("Batchid call initiated at MediaBatchCreation", batchId)
			fetchBatchDetails(batchId);
		}
	}, [batchId]);

	useEffect(() => {
		getMixingRatio();
	}, [creationPayload.media_master_id, creationPayload.intended_use_id]);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaBatchInit.title")} />
			<div className="flex flex-col h-full bg-[#F3F6F9]">
				<div className="p-6 flex-1 overflow-y-auto">
					<div className="grid grid-cols-12 gap-3 items-start">
						{/* Main Form Column */}
						<div className="col-span-12 lg:col-span-8 space-y-5">
							{/* 01 Batch Identity */}
							<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
								<div className="px-6 py-2 border-b border-slate-100 flex items-center gap-2">
									<div className="w-6 h-6 rounded-md bg-[#2D3E50] text-white flex items-center justify-center text-[10px] font-black">
										01
									</div>
									<h3 className="text-[10px] font-black text-[#2D3E50] uppercase tracking-widest mt-2">
										{t("mediaBatchInit.creation.batchIdentity")}
									</h3>
								</div>
								<div className="p-6">
									<div className="bg-[#FFF8F3] border border-orange-100 rounded-2xl p-4 flex items-center justify-between shadow-sm mb-3">
										<div className="flex items-center gap-8">
											<div className="w-8 h-8 bg-orange-100/50 rounded-xl flex items-center justify-center">
												<span className="material-symbols-outlined text-orange-500 text-3xl font-light">
													qr_code_2
												</span>
											</div>
											<div className="flex flex-col">
												<p className="text-[10px] font-black text-orange-400 uppercase tracking-widest mb-1">
													{t(
														"mediaBatchInit.creation.systemIdLabel",
													)}
												</p>
												<h3 className="text-xl font-black text-[#2D3E50] tracking-tight">
													{mediaBatchData?.media_batch_identifier ||
														""}
												</h3>
											</div>
										</div>
										<button
											className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-800 rounded-xl text-[10px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm disabled:opacity-50"
											disabled={
												!mediaBatchData?.media_batch_identifier
											}
											onClick={() =>
												navigator.clipboard.writeText(
													mediaBatchData?.media_batch_identifier ||
														"",
												)
											}
										>
											<span className="material-symbols-outlined text-sm">
												content_copy
											</span>
											{t("mediaBatchInit.creation.copyId")}
										</button>
									</div>

									<div className="space-y-2">
										<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
											{t("mediaBatchInit.creation.batchDescription")}  <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={
												mediaBatchData?.media_batch_description ||
												creationPayload.media_batch_description
											}
											onChange={(e) =>
												handleChange(
													"media_batch_description",
													e.target.value,
												)
											}
											placeholder={t(
												"mediaBatchInit.creation.descriptionPlaceholder",
											)}
											disabled={isReadOnly}
											readOnly={isReadOnly}
											className={`w-full px-4 py-3 bg-slate-50/50 border border-slate-100 rounded-lg text-xs text-slate-800 outline-none focus:border-orange-500 focus:bg-white transition-all shadow-inner ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
										/>
										{errors.media_batch_description && <p className="mt-1 text-[11px] text-red-500">{errors.media_batch_description}</p>}
									</div>
								</div>
							</div>

							{/* 02 Media Selection & Specifications */}
							<div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
								<div className="px-6 py-2 border-b border-slate-100 flex items-center gap-2">
									<div className="w-6 h-6 rounded-md bg-[#2D3E50] text-white flex items-center justify-center text-[10px] font-black">
										02
									</div>
									<h3 className="text-[10px] font-black text-[#2D3E50] uppercase tracking-widest mt-2">
										{t(
											"mediaBatchInit.creation.mediaSelectionSectionLabel",
										)}
									</h3>
								</div>
								<div className="p-6 space-y-4">
									<div className="grid grid-cols-2 gap-8">
										<div className="space-y-2">
											<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
												{t(
													"mediaBatchInit.creation.mediaTypeLabel",
												)}  <span className="text-red-500">*</span>
											</label>
											<div className="relative">
												<select
													value={
														mediaBatchData?.media_master_id ||
														creationPayload.media_master_id
													}
													onChange={(e) =>
														handleChange(
															"media_master_id",
															e.target.value,
														)
													}
													disabled={isReadOnly}
													className={`w-full pl-4 pr-10 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-800 focus:ring-2 focus:ring-orange-500/10 focus:border-orange-500 outline-none appearance-none transition-all ${
														isReadOnly
															? "bg-slate-100 opacity-60 cursor-not-allowed pointer-events-none"
															: "bg-slate-50 cursor-pointer"
													}`}
												>
													<option value="" disabled>
														{t(
															"mediaBatchInit.creation.mediaTypePlaceholder",
														)}
													</option>
													{mediaMasterList.map((media) => (
														<option
															key={media.id}
															value={media.id}
														>
															{media.plate_type}
														</option>
													))}
												</select>
												<span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-300">
													science
												</span>
												{errors.media_master_id && <p className="mt-1 text-[11px] text-red-500">{errors.media_master_id}</p>}
											</div>
										</div>
										<div className="space-y-2">
											<label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
												{t(
													"mediaBatchInit.creation.intendedUseLabel",
												)}  <span className="text-red-500">*</span>
											</label>
											<div className="relative">
												<select
													value={
														mediaBatchData?.intended_use_id ||
														creationPayload.intended_use_id
													}
													onChange={(e) =>
														handleChange(
															"intended_use_id",
															e.target.value,
														)
													}
													disabled={isReadOnly}
													className={`w-full pl-4 pr-10 py-2.5 border border-slate-200 rounded-lg text-xs text-slate-800 focus:ring-2 focus:ring-orange-500/10 focus:border-orange-500 outline-none appearance-none transition-all ${
														isReadOnly
															? "bg-slate-100 opacity-60 cursor-not-allowed pointer-events-none"
															: "bg-slate-50 cursor-pointer"
													}`}
												>
													<option value="" disabled>
														{t(
															"mediaBatchInit.creation.intendedUsePlaceholder",
														)}
													</option>
													{masterActivitiesList.map((activity) => (
														<option
															key={activity.id}
															value={activity.id}
														>
															{activity.name}
														</option>
													))}
												</select>
												<span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-300">
													biotech
												</span>
												{errors.intended_use_id && <p className="mt-1 text-[11px] text-red-500">{errors.intended_use_id}</p>}
											</div>
										</div>
									</div>
									<div className="bg-[#F8FAFC] border border-slate-100 rounded-2xl p-3 flex flex-wrap gap-6 items-center justify-center">
										<div className="h-5 w-px bg-slate-200 hidden md:block"></div>
										<div className="text-center">
											<p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
												SOP Mixing Ratio
											</p>
											<div className="flex items-center gap-2 bg-[#E3F2FD] text-[#1976D2] border border-[#BBDEFB] px-4 py-2 rounded-xl text-xs font-black">
												<div className="w-2 h-2 rounded-full bg-[#1976D2]"></div>
												{mediaBatchData?.sop_mixing_ratio ||
													creationPayload.sop_mixing_ratio ||
													"N/A"}{" "}
												(P:W)
											</div>
										</div>
										<div className="h-10 w-px bg-slate-200 hidden md:block"></div>
									</div>
								</div>
							</div>
						</div>

						{/* Sidebar Column */}
						<div className="col-span-12 lg:col-span-4 space-y-2 h-full">
							{/* 03 Lot Details & Validation */}
							<MBCProfileCard
								initiatedDate={mediaBatchData?.created_at}
								remarks={
									mediaBatchData?.initial_remarks ??
									creationPayload.remarks
								}
								onRemarksChange={(value) =>handleChange("remarks", value)
								}
								readOnly={isReadOnly}
							/>
						</div>
					</div>
				</div>

				<MBCFooter
					creationPayload={creationPayload}
					onNext={onNext}
					showToast={showToast}
					mediaBatchData={mediaBatchData}
					setBatchId={setBatchId}
					validateFields={validateFields}
				/>
			</div>
			<CustomToast
				show={toast.show}
				message={toast.message}
				toastType={toast.toastType}
				delay={2400}
				onClose={() => setToast((prev) => ({ ...prev, show: false }))}
				toastPosition="topRight"
			/>
		</div>
	);
};

export default MediaBatchCreation;
